import { useCallback, useRef, useState } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";

// Single-thread build (does not require SharedArrayBuffer / COOP+COEP headers)
const CORE_URL_BASE = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd";

/**
 * Hook that compresses a video File using ffmpeg.wasm entirely in the browser.
 *
 * Returns: { compress, loading, progress, ready }
 *   - compress(file, opts?) -> Promise<File | null>
 *   - progress: 0..1 during a compression run
 */
export default function useVideoCompressor() {
  const ffmpegRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = useCallback(async () => {
    if (ffmpegRef.current) return ffmpegRef.current;
    const ffmpeg = new FFmpeg();
    ffmpeg.on("progress", ({ progress: p }) => {
      if (typeof p === "number" && !Number.isNaN(p)) {
        setProgress(Math.min(0.99, Math.max(0, p)));
      }
    });
    await ffmpeg.load({
      coreURL: await toBlobURL(`${CORE_URL_BASE}/ffmpeg-core.js`, "text/javascript"),
      wasmURL: await toBlobURL(`${CORE_URL_BASE}/ffmpeg-core.wasm`, "application/wasm"),
    });
    ffmpegRef.current = ffmpeg;
    setReady(true);
    return ffmpeg;
  }, []);

  const compress = useCallback(async (file, opts = {}) => {
    if (!file) return null;
    const { targetHeight = 480, videoBitrate = "800k", audioBitrate = "96k" } = opts;
    setLoading(true);
    setProgress(0);
    try {
      const ffmpeg = await load();
      const ext = (file.name.split(".").pop() || "mp4").toLowerCase();
      const inputName = `in.${ext}`;
      const outputName = "out.mp4";
      await ffmpeg.writeFile(inputName, await fetchFile(file));
      await ffmpeg.exec([
        "-i", inputName,
        "-vf", `scale=-2:${targetHeight}`,
        "-c:v", "libx264",
        "-preset", "veryfast",
        "-b:v", videoBitrate,
        "-c:a", "aac",
        "-b:a", audioBitrate,
        "-movflags", "+faststart",
        outputName,
      ]);
      const data = await ffmpeg.readFile(outputName);
      // clean up virtual FS
      try { await ffmpeg.deleteFile(inputName); } catch {}
      try { await ffmpeg.deleteFile(outputName); } catch {}
      const compressedName = file.name.replace(/\.[^.]+$/, "") + "-compressed.mp4";
      const blob = new Blob([data.buffer], { type: "video/mp4" });
      setProgress(1);
      return new File([blob], compressedName, { type: "video/mp4" });
    } finally {
      setLoading(false);
    }
  }, [load]);

  return { compress, loading, progress, ready };
}
